import { useAtom } from "jotai";
import axios from "axios";
import { useRef, useState } from "react";
import { useLogin } from "../login";
import { environment } from "../../environment";
import { errorCatcher } from "../../hooks";
import {
    BrandDashboardPayload,
    BrandRankingRow,
    FulfillmentPipelineStage,
    GlobalKPIs,
    RecentDesign,
    RecentOrder,
    SystemHealthStatus,
    brandRankingsAtom,
    brandRankingsTotalAtom,
    dashboardFetchTimesAtom,
    dashboardPayloadAtom,
    fulfillmentPipelineAtom,
    globalKPIsAtom,
    recentDesignsAtom,
    recentOrdersAtom,
    systemHealthAtom
} from "./types";

const brandQuery = (brandId?: number | null) => (brandId != null ? `?brand_id=${brandId}` : "");

export const useDashboard = () => {
    const [, authToken, loginFunctions] = useLogin();
    const [payload, setPayload] = useAtom(dashboardPayloadAtom);
    const [globalKPIs, setGlobalKPIs] = useAtom(globalKPIsAtom);
    const [recentOrders, setRecentOrders] = useAtom(recentOrdersAtom);
    const [recentDesigns, setRecentDesigns] = useAtom(recentDesignsAtom);
    const [pipeline, setPipeline] = useAtom(fulfillmentPipelineAtom);
    const [systemHealth, setSystemHealth] = useAtom(systemHealthAtom);
    const [brandRankings, setBrandRankings] = useAtom(brandRankingsAtom);
    const [brandRankingsTotal, setBrandRankingsTotal] = useAtom(brandRankingsTotalAtom);
    const [fetchTimes, setFetchTimes] = useAtom(dashboardFetchTimesAtom);

    const [kpisLoading, setKpisLoading] = useState(false);
    const [globalKpisLoading, setGlobalKpisLoading] = useState(false);
    const [recentOrdersLoading, setRecentOrdersLoading] = useState(false);
    const [recentDesignsLoading, setRecentDesignsLoading] = useState(false);
    const [pipelineLoading, setPipelineLoading] = useState(false);
    const [systemHealthLoading, setSystemHealthLoading] = useState(false);
    const [brandRankingsLoading, setBrandRankingsLoading] = useState(false);

    // Per-endpoint request-id counters. When useEffect re-fires before a
    // previous fetch resolves (e.g. brand-filter atom hydrates from null→1
    // milliseconds after initial render, or user rapid-clicks date presets),
    // multiple requests race. Without this guard, the slowest response wins
    // and can overwrite a correctly-filtered response with a stale one —
    // exactly the bug that made Wall Blush's revenue card flip from ~$220k
    // to the all-brand $11.9M aggregate a few seconds after page load.
    const kpisReqId        = useRef(0);
    const globalKpisReqId  = useRef(0);
    const recentOrdersReqId = useRef(0);
    const recentDesignsReqId = useRef(0);
    const pipelineReqId    = useRef(0);
    const systemHealthReqId = useRef(0);
    const brandRankingsReqId = useRef(0);

    const headers = { "X-Wallmates-Auth": authToken };

    const stampFetch = (key: keyof typeof fetchTimes) =>
        setFetchTimes((prev) => ({ ...prev, [key]: Date.now() }));

    const getKPIs = async (
        brandId?: number | null,
        startISO?: string,
        endISO?: string,
        compare: boolean = false
    ): Promise<BrandDashboardPayload | null> => {
        const myReqId = ++kpisReqId.current;
        setKpisLoading(true);
        try {
            const params: string[] = [];
            if (brandId != null) params.push(`brand_id=${brandId}`);
            if (startISO) params.push(`start=${encodeURIComponent(startISO)}`);
            if (endISO)   params.push(`end=${encodeURIComponent(endISO)}`);
            if (compare)  params.push(`compare=true`);
            const query = params.length ? `?${params.join("&")}` : "";
            const res = await axios
                .get(`${environment.v2Url}/dashboard/kpis${query}`, { headers })
                .catch((err: any) => errorCatcher(err, loginFunctions.logout));
            if (myReqId !== kpisReqId.current) return null; // a newer request superseded us — drop response
            if (!res) return null;
            setPayload(res.data);
            stampFetch("kpis");
            return res.data;
        } finally {
            if (myReqId === kpisReqId.current) setKpisLoading(false);
        }
    };

    const getGlobalKPIs = async (startISO?: string, endISO?: string): Promise<GlobalKPIs | null> => {
        const myReqId = ++globalKpisReqId.current;
        setGlobalKpisLoading(true);
        try {
            const params: string[] = [];
            if (startISO) params.push(`start=${encodeURIComponent(startISO)}`);
            if (endISO)   params.push(`end=${encodeURIComponent(endISO)}`);
            const query = params.length ? `?${params.join("&")}` : "";
            const res = await axios
                .get(`${environment.v2Url}/dashboard/kpis/global${query}`, { headers })
                .catch((err: any) => errorCatcher(err, loginFunctions.logout));
            if (myReqId !== globalKpisReqId.current) return null;
            if (!res) return null;
            setGlobalKPIs(res.data);
            stampFetch("globalKpis");
            return res.data;
        } finally {
            if (myReqId === globalKpisReqId.current) setGlobalKpisLoading(false);
        }
    };

    const getRecentOrders = async (brandId?: number | null): Promise<RecentOrder[]> => {
        const myReqId = ++recentOrdersReqId.current;
        setRecentOrdersLoading(true);
        try {
            const res = await axios
                .get(`${environment.v2Url}/dashboard/activity/orders${brandQuery(brandId)}`, { headers })
                .catch((err: any) => errorCatcher(err, loginFunctions.logout));
            if (myReqId !== recentOrdersReqId.current) return [];
            if (!res) return [];
            setRecentOrders(res.data);
            stampFetch("recentOrders");
            return res.data;
        } finally {
            if (myReqId === recentOrdersReqId.current) setRecentOrdersLoading(false);
        }
    };

    const getRecentDesigns = async (brandId?: number | null): Promise<RecentDesign[]> => {
        const myReqId = ++recentDesignsReqId.current;
        setRecentDesignsLoading(true);
        try {
            const res = await axios
                .get(`${environment.v2Url}/dashboard/activity/designs${brandQuery(brandId)}`, { headers })
                .catch((err: any) => errorCatcher(err, loginFunctions.logout));
            if (myReqId !== recentDesignsReqId.current) return [];
            if (!res) return [];
            setRecentDesigns(res.data);
            stampFetch("recentDesigns");
            return res.data;
        } finally {
            if (myReqId === recentDesignsReqId.current) setRecentDesignsLoading(false);
        }
    };

    const getPipeline = async (brandId?: number | null, startISO?: string, endISO?: string): Promise<FulfillmentPipelineStage[]> => {
        const myReqId = ++pipelineReqId.current;
        setPipelineLoading(true);
        try {
            const params: string[] = [];
            if (brandId != null) params.push(`brand_id=${brandId}`);
            if (startISO) params.push(`start=${encodeURIComponent(startISO)}`);
            if (endISO)   params.push(`end=${encodeURIComponent(endISO)}`);
            const query = params.length ? `?${params.join("&")}` : "";
            const res = await axios
                .get(`${environment.v2Url}/dashboard/pipeline${query}`, { headers })
                .catch((err: any) => errorCatcher(err, loginFunctions.logout));
            if (myReqId !== pipelineReqId.current) return [];
            if (!res) return [];
            setPipeline(res.data);
            stampFetch("pipeline");
            return res.data;
        } finally {
            if (myReqId === pipelineReqId.current) setPipelineLoading(false);
        }
    };

    const getSystemHealth = async (): Promise<SystemHealthStatus[]> => {
        const myReqId = ++systemHealthReqId.current;
        setSystemHealthLoading(true);
        try {
            const res = await axios
                .get(`${environment.v2Url}/dashboard/system-health`, { headers })
                .catch((err: any) => errorCatcher(err, loginFunctions.logout));
            if (myReqId !== systemHealthReqId.current) return [];
            if (!res) return [];
            setSystemHealth(res.data);
            stampFetch("systemHealth");
            return res.data;
        } finally {
            if (myReqId === systemHealthReqId.current) setSystemHealthLoading(false);
        }
    };

    const getBrandRankings = async (startISO?: string, endISO?: string, page?: number, pageSize?: number, sortBy?: string, sortDir?: string, search?: string, activityOnly?: boolean): Promise<BrandRankingRow[]> => {
        const myReqId = ++brandRankingsReqId.current;
        setBrandRankingsLoading(true);
        try {
            const params: string[] = [];
            if (startISO) params.push(`start=${encodeURIComponent(startISO)}`);
            if (endISO)   params.push(`end=${encodeURIComponent(endISO)}`);
            if (page !== undefined) params.push(`page=${page}`);
            if (pageSize !== undefined) params.push(`pageSize=${pageSize}`);
            if (sortBy) params.push(`sortBy=${encodeURIComponent(sortBy)}`);
            if (sortDir) params.push(`sortDir=${sortDir}`);
            if (search) params.push(`search=${encodeURIComponent(search)}`);
            if (activityOnly !== undefined) params.push(`activityOnly=${activityOnly ? "true" : "false"}`);
            const query = params.length ? `?${params.join("&")}` : "";
            const res = await axios
                .get(`${environment.v2Url}/dashboard/brand-rankings${query}`, { headers })
                .catch((err: any) => errorCatcher(err, loginFunctions.logout));
            if (myReqId !== brandRankingsReqId.current) return [];
            if (!res) return [];
            // Extract brands from the paginated response
            const brands = res.data.brands || res.data;
            const total = typeof res.data?.total_count === "number"
                ? res.data.total_count
                : (Array.isArray(brands) ? brands.length : 0);
            setBrandRankings(brands);
            setBrandRankingsTotal(total);
            stampFetch("brandRankings");
            return brands;
        } finally {
            if (myReqId === brandRankingsReqId.current) setBrandRankingsLoading(false);
        }
    };

    return {
        payload,
        globalKPIs,
        recentOrders,
        recentDesigns,
        pipeline,
        systemHealth,
        brandRankings,
        brandRankingsTotal,
        fetchTimes,
        kpisLoading,
        globalKpisLoading,
        recentOrdersLoading,
        recentDesignsLoading,
        pipelineLoading,
        systemHealthLoading,
        brandRankingsLoading,
        getKPIs,
        getGlobalKPIs,
        getRecentOrders,
        getRecentDesigns,
        getPipeline,
        getSystemHealth,
        getBrandRankings
    };
};
